import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Animal } from '@/api/animal'
import { animalService } from '@/api/animal'

export const useAnimalStore = defineStore('animal', () => {
  const animals = ref<Animal[]>([])
  const allAnimals = ref<Animal[]>([])
  const totalCount = ref(0)
  const currentPage = ref(1)
  const pageSize = ref(12)
  const searchQuery = ref('')
  const loading = ref(false)
  const error = ref<string | null>(null)
  const success = ref<string | null>(null)

  async function fetchAnimals(params?: { page?: number; limit?: number; search?: string }): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const response = await animalService.getAnimals({
        page: params?.page ?? currentPage.value,
        limit: params?.limit ?? pageSize.value,
        search: params?.search ?? searchQuery.value,
      })

      const data = response.data

      if (data && typeof data === 'object') {
        if ('results' in data && Array.isArray(data.results)) {
          animals.value = data.results
          totalCount.value = data.count
          currentPage.value = params?.page ?? currentPage.value
          pageSize.value = params?.limit ?? pageSize.value
          searchQuery.value = params?.search ?? searchQuery.value
          return
        }

        // Fallback for non-paginated or differently structured responses
        if (Array.isArray(data)) {
          animals.value = data
          totalCount.value = data.length
          return
        }
      }

      animals.value = []
      totalCount.value = 0
    } catch (err: unknown) {
      if (err instanceof Error) {
        error.value = err.message
      } else if (typeof err === 'object' && err !== null && 'response' in err) {
        const e = err as { response?: { data?: { message?: string } } }
        error.value = e.response?.data?.message ?? 'Failed to fetch animals.'
      } else {
        error.value = 'Failed to fetch animals.'
      }
    } finally {
      loading.value = false
    }
  }

  async function fetchAllAnimals(): Promise<Animal[]> {
    loading.value = true
    error.value = null
    try {
      const response = await animalService.getAnimals({ limit: 10000 })
      const data = response.data
      let list: Animal[] = []
      if (data && typeof data === 'object') {
        if ('results' in data && Array.isArray(data.results)) {
          list = data.results
        } else if (Array.isArray(data)) {
          list = data
        }
      }
      allAnimals.value = list
      if (animals.value.length === 0) {
        animals.value = list
      }
      return list
    } catch (err: unknown) {
      console.error('Error fetching all animals:', err)
      return allAnimals.value
    } finally {
      loading.value = false
    }
  }

  async function registerAnimal(payload: Omit<Animal, 'id'>): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const res = await animalService.registerAnimal(payload)
      const normalizeItem = (item: unknown): Animal | null => {
        if (!item) return null
        if (typeof item === 'object') {
          const it = item as Record<string, unknown>
          if ('data' in it && it.data && typeof it.data === 'object') return it.data as Animal
          return item as Animal
        }
        return null
      }

      const created = normalizeItem(res.data)
      if (created) animals.value.push(created)
      success.value = 'Livestock registered successfully!'
    } catch (err: unknown) {
      if (err instanceof Error) {
        error.value = err.message
      } else if (typeof err === 'object' && err !== null && 'response' in err) {
        const e = err as { response?: { data?: { message?: string } } }
        error.value = e.response?.data?.message ?? 'Failed to register animal.'
      } else {
        error.value = 'Failed to register animal.'
      }
    } finally {
      loading.value = false
    }
  }

  async function updateAnimal(id: string | number, payload: Partial<Animal>): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const idStr = String(id)
      const res = await animalService.updateAnimal(idStr, payload)
      const idx = animals.value.findIndex((a) => String(a.id) === idStr)
      if (idx !== -1) animals.value[idx] = res.data as Animal
      success.value = 'Animal updated.'
    } catch (err: unknown) {
      if (err instanceof Error) {
        error.value = err.message
      } else if (typeof err === 'object' && err !== null && 'response' in err) {
        const e = err as { response?: { data?: { message?: string } } }
        error.value = e.response?.data?.message ?? 'Failed to update animal.'
      } else {
        error.value = 'Failed to update animal.'
      }
    } finally {
      loading.value = false
    }
  }

  async function deleteAnimal(id: string | number): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const idStr = String(id)
      await animalService.deleteAnimal(idStr)
      animals.value = animals.value.filter((a) => String(a.id) !== idStr)
      success.value = 'Animal deleted.'
    } catch (err: unknown) {
      if (err instanceof Error) {
        error.value = err.message
      } else if (typeof err === 'object' && err !== null && 'response' in err) {
        const e = err as { response?: { data?: { message?: string } } }
        error.value = e.response?.data?.message ?? 'Failed to delete animal.'
      } else {
        error.value = 'Failed to delete animal.'
      }
    } finally {
      loading.value = false
    }
  }

  return {
    animals,
    allAnimals,
    totalCount,
    currentPage,
    pageSize,
    searchQuery,
    loading,
    error,
    success,
    fetchAnimals,
    fetchAllAnimals,
    registerAnimal,
    updateAnimal,
    deleteAnimal,
  }
})
